import React, { useState, useEffect } from "react";
import {
    Modal, Upload, message, Image, Typography, Spin, Empty,
    Timeline, Button, Input, DatePicker, Popconfirm, Tooltip,
} from "antd";
import {
    PlusOutlined, DeleteOutlined, EyeOutlined, EditOutlined,
    CheckOutlined, CloseOutlined, CameraOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { uldService } from "../../../../api/enhancedApi";
import { uploadFile, attachmentApi } from "../../../../api/attachmentApi";

const { Text } = Typography;

const MAX_PHOTOS_TOTAL = 30;
const MAX_FILE_SIZE = 10 * 1024 * 1024;
const ACCEPTED_TYPES = ["image/jpeg", "image/png", "image/jpg"];

const generateId = () =>
    typeof crypto !== "undefined" && crypto.randomUUID
        ? crypto.randomUUID()
        : `evt_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;

const autoTitle = (date) => dayjs(date).format("YYYY-MM-DD") + " 的记录";

const getTotalPhotos = (events) =>
    events.reduce((sum, ev) => sum + (ev.items?.length || 0), 0);

// ─── 新建事件表单（日期 + 备注 + 照片，一步完成）───
const NewEventForm = ({ onAdd, onCancel, totalPhotos }) => {
    const [remarks, setRemarks] = useState("");
    const [eventDate, setEventDate] = useState(dayjs());
    const [uploadedItems, setUploadedItems] = useState([]);
    const [uploadedUrls, setUploadedUrls] = useState({});
    const [uploading, setUploading] = useState(false);

    const canAddMore = totalPhotos + uploadedItems.length < MAX_PHOTOS_TOTAL;

    const beforeUpload = (file) => {
        if (!ACCEPTED_TYPES.includes(file.type)) {
            message.error("仅支持 JPG/PNG 格式图片");
            return Upload.LIST_IGNORE;
        }
        if (file.size > MAX_FILE_SIZE) {
            message.error("单张图片不能超过 10MB");
            return Upload.LIST_IGNORE;
        }
        if (!canAddMore) {
            message.error(`照片总数不能超过 ${MAX_PHOTOS_TOTAL} 张`);
            return Upload.LIST_IGNORE;
        }
        return true;
    };

    const handleCustomUpload = async ({ file, onSuccess, onError }) => {
        setUploading(true);
        try {
            const result = await uploadFile(file);
            const newPhoto = {
                object_key: result.storage_path,
                filename: file.name,
                caption: null,
                content_type: result.content_type,
                file_size: result.file_size,
                uploaded_by: null,
                uploaded_at: new Date().toISOString(),
            };
            setUploadedItems((prev) => [...prev, newPhoto]);
            const data = await attachmentApi.getPresignedViewUrl(result.storage_path);
            setUploadedUrls((prev) => ({ ...prev, [result.storage_path]: data.signed_url }));
            onSuccess(result);
        } catch (err) {
            console.error("上传失败:", err);
            message.error("照片上传失败");
            onError(err);
        } finally {
            setUploading(false);
        }
    };

    const handleRemoveItem = (objectKey) => {
        setUploadedItems((prev) => prev.filter((p) => p.object_key !== objectKey));
        setUploadedUrls((prev) => {
            const next = { ...prev };
            delete next[objectKey];
            return next;
        });
    };

    const handleSubmit = () => {
        if (uploadedItems.length === 0) {
            message.warning("请至少上传一张照片");
            return;
        }
        const title = remarks.trim() || autoTitle(eventDate);
        onAdd(
            {
                event_id: generateId(),
                title,
                description: null,
                event_date: eventDate.toISOString(),
                created_by: null,
                created_at: new Date().toISOString(),
                items: uploadedItems,
            },
            uploadedUrls
        );
    };

    return (
        <div style={{
            border: "1px solid #1677ff", borderRadius: 8, padding: 16,
            marginBottom: 16, background: "#f0f7ff",
        }}>
            {/* 日期 + 备注 */}
            <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
                <DatePicker
                    value={eventDate}
                    onChange={(v) => v && setEventDate(v)}
                    allowClear={false}
                    style={{ width: 148 }}
                />
                <Input
                    placeholder="备注（可选）"
                    value={remarks}
                    onChange={(e) => setRemarks(e.target.value)}
                    maxLength={200}
                    style={{ flex: 1 }}
                />
            </div>

            {/* 照片上传区 */}
            <Spin spinning={uploading} tip="上传中...">
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 12, minHeight: 88 }}>
                    {uploadedItems.map((photo) => (
                        <div
                            key={photo.object_key}
                            style={{
                                width: 80, height: 80,
                                border: "1px solid #d9d9d9", borderRadius: 6,
                                overflow: "hidden", position: "relative",
                                flexShrink: 0,
                            }}
                        >
                            <img
                                src={uploadedUrls[photo.object_key] || ""}
                                alt={photo.filename}
                                style={{ width: "100%", height: "100%", objectFit: "cover" }}
                            />
                            <div
                                style={{
                                    position: "absolute", top: 2, right: 2,
                                    background: "rgba(0,0,0,0.55)", borderRadius: "50%",
                                    width: 18, height: 18, display: "flex",
                                    alignItems: "center", justifyContent: "center",
                                    cursor: "pointer",
                                }}
                                onClick={() => handleRemoveItem(photo.object_key)}
                            >
                                <CloseOutlined style={{ color: "#fff", fontSize: 10 }} />
                            </div>
                        </div>
                    ))}
                    {canAddMore && (
                        <Upload
                            accept=".jpg,.jpeg,.png"
                            showUploadList={false}
                            beforeUpload={beforeUpload}
                            customRequest={handleCustomUpload}
                            multiple
                        >
                            <div style={{
                                width: 80, height: 80,
                                border: "1px dashed #1677ff", borderRadius: 6,
                                display: "flex", flexDirection: "column",
                                alignItems: "center", justifyContent: "center",
                                cursor: "pointer", background: "#fff", flexShrink: 0,
                            }}>
                                <PlusOutlined style={{ fontSize: 18, color: "#1677ff" }} />
                                <div style={{ marginTop: 4, color: "#1677ff", fontSize: 11 }}>上传照片</div>
                            </div>
                        </Upload>
                    )}
                </div>
            </Spin>

            {/* 操作按钮 */}
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
                <Button size="small" icon={<CloseOutlined />} onClick={onCancel}>取消</Button>
                <Button
                    type="primary"
                    size="small"
                    icon={<CheckOutlined />}
                    onClick={handleSubmit}
                    disabled={uploadedItems.length === 0 || uploading}
                >
                    {uploadedItems.length > 0 ? `添加记录（${uploadedItems.length} 张）` : "添加记录（请先上传照片）"}
                </Button>
            </div>
        </div>
    );
};

// ─── 单张照片卡片 ───
const PhotoCard = ({ photo, signedUrl, onRemove, onPreview, onCaptionChange }) => {
    const [editingCaption, setEditingCaption] = useState(false);
    const [captionText, setCaptionText] = useState(photo.caption || "");

    const saveCaption = () => {
        onCaptionChange(captionText.trim() || null);
        setEditingCaption(false);
    };

    return (
        <div style={{ width: 100, textAlign: "center" }}>
            <div style={{
                width: 100, height: 100,
                border: "1px solid #d9d9d9", borderRadius: 8,
                overflow: "hidden", position: "relative", background: "#fafafa",
            }}>
                <img
                    src={signedUrl || ""}
                    alt={photo.filename}
                    style={{ width: "100%", height: "100%", objectFit: "cover", cursor: "pointer" }}
                    onClick={() => onPreview(photo.object_key)}
                />
                <div style={{
                    position: "absolute", bottom: 0, left: 0, right: 0,
                    background: "rgba(0,0,0,0.5)", display: "flex",
                    justifyContent: "center", gap: 12, padding: "4px 0",
                }}>
                    <EyeOutlined
                        style={{ color: "#fff", cursor: "pointer", fontSize: 14 }}
                        onClick={() => onPreview(photo.object_key)}
                    />
                    <DeleteOutlined
                        style={{ color: "#ff4d4f", cursor: "pointer", fontSize: 14 }}
                        onClick={() => onRemove(photo.object_key)}
                    />
                </div>
            </div>
            {editingCaption ? (
                <Input
                    size="small"
                    value={captionText}
                    onChange={(e) => setCaptionText(e.target.value)}
                    onBlur={saveCaption}
                    onPressEnter={saveCaption}
                    placeholder="照片说明"
                    maxLength={100}
                    style={{ marginTop: 4, fontSize: 11 }}
                    autoFocus
                />
            ) : (
                <Tooltip title="点击编辑说明">
                    <div
                        onClick={() => setEditingCaption(true)}
                        style={{
                            marginTop: 4, fontSize: 11,
                            color: photo.caption ? "#333" : "#bbb",
                            cursor: "pointer", overflow: "hidden",
                            textOverflow: "ellipsis", whiteSpace: "nowrap",
                        }}
                    >
                        {photo.caption || "添加说明..."}
                    </div>
                </Tooltip>
            )}
        </div>
    );
};

// ─── 单个事件卡片（展示 + 编辑已有记录）───
const EventCard = ({
    event, signedUrls, totalPhotos,
    onRemoveEvent, onUpdateEvent, onUploadPhoto, onRemovePhoto,
    onPreview, onCaptionChange, uploading,
}) => {
    const [editingRemarks, setEditingRemarks] = useState(false);
    const [remarksText, setRemarksText] = useState(event.title || "");

    const canAddMore = totalPhotos < MAX_PHOTOS_TOTAL;
    const eventDate = event.event_date ? dayjs(event.event_date).format("YYYY-MM-DD") : "未知日期";

    const saveRemarks = () => {
        const newTitle = remarksText.trim() || autoTitle(event.event_date || new Date());
        onUpdateEvent({ ...event, title: newTitle });
        setEditingRemarks(false);
    };

    const beforeUpload = (file) => {
        if (!ACCEPTED_TYPES.includes(file.type)) {
            message.error("仅支持 JPG/PNG 格式图片");
            return Upload.LIST_IGNORE;
        }
        if (file.size > MAX_FILE_SIZE) {
            message.error("单张图片不能超过 10MB");
            return Upload.LIST_IGNORE;
        }
        if (!canAddMore) {
            message.error(`照片总数不能超过 ${MAX_PHOTOS_TOTAL} 张`);
            return Upload.LIST_IGNORE;
        }
        return true;
    };

    const handleCustomUpload = async ({ file, onSuccess: onUploadSuccess, onError }) => {
        try {
            await onUploadPhoto(event.event_id, file, onUploadSuccess, onError);
        } catch (err) {
            onError(err);
        }
    };

    return (
        <div style={{ marginBottom: 4 }}>
            {/* 日期 + 备注行 */}
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                <Text type="secondary" style={{ fontSize: 12, whiteSpace: "nowrap" }}>{eventDate}</Text>
                {editingRemarks ? (
                    <Input
                        size="small"
                        value={remarksText}
                        onChange={(e) => setRemarksText(e.target.value)}
                        onBlur={saveRemarks}
                        onPressEnter={saveRemarks}
                        placeholder="备注（可选）"
                        maxLength={200}
                        style={{ flex: 1 }}
                        autoFocus
                    />
                ) : (
                    <Text
                        style={{ flex: 1, cursor: "pointer" }}
                        onClick={() => setEditingRemarks(true)}
                    >
                        {event.title}
                    </Text>
                )}
                {!editingRemarks && (
                    <Tooltip title="编辑备注">
                        <EditOutlined
                            style={{ cursor: "pointer", color: "#1677ff", fontSize: 13 }}
                            onClick={() => setEditingRemarks(true)}
                        />
                    </Tooltip>
                )}
                <Popconfirm
                    title="确认删除这条记录及其所有照片？"
                    onConfirm={() => onRemoveEvent(event.event_id)}
                    okText="删除"
                    cancelText="取消"
                    okButtonProps={{ danger: true }}
                >
                    <Tooltip title="删除此记录">
                        <DeleteOutlined style={{ cursor: "pointer", color: "#ff4d4f", fontSize: 13 }} />
                    </Tooltip>
                </Popconfirm>
            </div>

            {/* 照片网格 */}
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {(event.items || []).map((photo) => (
                    <PhotoCard
                        key={photo.object_key}
                        photo={photo}
                        signedUrl={signedUrls[photo.object_key]}
                        onRemove={(key) => onRemovePhoto(event.event_id, key)}
                        onPreview={onPreview}
                        onCaptionChange={(caption) => onCaptionChange(event.event_id, photo.object_key, caption)}
                    />
                ))}
                {canAddMore && (
                    <Upload
                        accept=".jpg,.jpeg,.png"
                        showUploadList={false}
                        beforeUpload={beforeUpload}
                        customRequest={handleCustomUpload}
                        multiple
                    >
                        <div style={{
                            width: 100, height: 100,
                            border: "1px dashed #d9d9d9", borderRadius: 8,
                            display: "flex", flexDirection: "column",
                            alignItems: "center", justifyContent: "center",
                            cursor: "pointer", background: "#fafafa",
                        }}>
                            <PlusOutlined style={{ fontSize: 18, color: "#999" }} />
                            <div style={{ marginTop: 4, color: "#999", fontSize: 11 }}>添加照片</div>
                        </div>
                    </Upload>
                )}
            </div>
        </div>
    );
};

// ─── 主组件 ───
const UldPhotoModal = ({ visible, onCancel, onSuccess, record }) => {
    const [loading, setLoading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [events, setEvents] = useState([]);
    const [previewVisible, setPreviewVisible] = useState(false);
    const [previewUrl, setPreviewUrl] = useState("");
    const [signedUrls, setSignedUrls] = useState({});
    const [showNewForm, setShowNewForm] = useState(false);

    useEffect(() => {
        if (visible && record) {
            setEvents([]);
            setSignedUrls({});
            setShowNewForm(false);
            fetchFreshPhotos(record.master.id);
        }
    }, [visible, record]);

    const fetchFreshPhotos = async (uldId) => {
        try {
            const response = await uldService.getUldDetail(uldId);
            const fresh = response.data?.master?.photos || [];
            setEvents(fresh);
            loadAllSignedUrls(fresh);
        } catch {
            // 降级：直接用 prop 里的数据
            const fallback = record?.master?.photos || [];
            setEvents(fallback);
            loadAllSignedUrls(fallback);
        }
    };

    const loadAllSignedUrls = async (evts) => {
        const allPhotos = evts.flatMap((ev) => ev.items || []);
        if (allPhotos.length === 0) return;
        const urls = {};
        await Promise.all(
            allPhotos.map(async (photo) => {
                try {
                    const data = await attachmentApi.getPresignedViewUrl(photo.object_key);
                    urls[photo.object_key] = data.signed_url;
                } catch {
                    urls[photo.object_key] = null;
                }
            })
        );
        setSignedUrls((prev) => ({ ...prev, ...urls }));
    };

    const handlePreview = async (objectKey) => {
        let url = signedUrls[objectKey];
        if (!url) {
            try {
                const data = await attachmentApi.getPresignedViewUrl(objectKey);
                url = data.signed_url;
                setSignedUrls((prev) => ({ ...prev, [objectKey]: url }));
            } catch {
                message.error("获取照片预览链接失败");
                return;
            }
        }
        setPreviewUrl(url);
        setPreviewVisible(true);
    };

    const handleAddEvent = (newEvent, newUrls) => {
        setEvents((prev) => [newEvent, ...prev]);
        setSignedUrls((prev) => ({ ...prev, ...newUrls }));
        setShowNewForm(false);
    };

    const handleRemoveEvent = (eventId) => {
        setEvents((prev) => prev.filter((ev) => ev.event_id !== eventId));
    };

    const handleUpdateEvent = (updated) => {
        setEvents((prev) => prev.map((ev) => (ev.event_id === updated.event_id ? updated : ev)));
    };

    const handleUploadPhoto = async (eventId, file, onUploadSuccess, onError) => {
        setUploading(true);
        try {
            const result = await uploadFile(file);
            const newPhoto = {
                object_key: result.storage_path,
                filename: file.name,
                caption: null,
                content_type: result.content_type,
                file_size: result.file_size,
                uploaded_by: null,
                uploaded_at: new Date().toISOString(),
            };
            setEvents((prev) =>
                prev.map((ev) =>
                    ev.event_id === eventId
                        ? { ...ev, items: [...(ev.items || []), newPhoto] }
                        : ev
                )
            );
            const data = await attachmentApi.getPresignedViewUrl(result.storage_path);
            setSignedUrls((prev) => ({ ...prev, [result.storage_path]: data.signed_url }));
            onUploadSuccess(result);
        } catch (error) {
            console.error("照片上传失败:", error);
            message.error("照片上传失败");
            onError(error);
        } finally {
            setUploading(false);
        }
    };

    const handleRemovePhoto = (eventId, objectKey) => {
        setEvents((prev) =>
            prev.map((ev) =>
                ev.event_id === eventId
                    ? { ...ev, items: (ev.items || []).filter((p) => p.object_key !== objectKey) }
                    : ev
            )
        );
    };

    const handleCaptionChange = (eventId, objectKey, caption) => {
        setEvents((prev) =>
            prev.map((ev) =>
                ev.event_id === eventId
                    ? {
                        ...ev,
                        items: (ev.items || []).map((p) =>
                            p.object_key === objectKey ? { ...p, caption } : p
                        ),
                    }
                    : ev
            )
        );
    };

    const handleSave = async () => {
        setLoading(true);
        try {
            const response = await uldService.updateUldPhotos(record.master.id, {
                photo_events: events,
            });
            if (response.data?.success) {
                message.success("照片保存成功");
                onSuccess?.();
            } else {
                message.error(response.data?.message || "保存失败");
            }
        } catch (error) {
            console.error("保存照片失败:", error);
            message.error(error.response?.data?.detail || "保存照片失败");
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        setEvents([]);
        setSignedUrls({});
        setPreviewVisible(false);
        setPreviewUrl("");
        setShowNewForm(false);
        onCancel();
    };

    const totalPhotos = getTotalPhotos(events);

    return (
        <Modal
            title={`管理照片 — ${record?.master?.uld_identifier || ""}`}
            open={visible}
            onCancel={handleCancel}
            onOk={handleSave}
            okText="保存"
            cancelText="取消"
            confirmLoading={loading}
            width={760}
            destroyOnHidden
            styles={{ body: { maxHeight: "68vh", overflowY: "auto" } }}
        >
            <Spin spinning={uploading} tip="上传中...">
                {/* 顶部操作栏 */}
                <div style={{
                    display: "flex", justifyContent: "space-between",
                    alignItems: "center", marginBottom: 12,
                }}>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        支持 JPG/PNG，单张不超过 10MB，共 {totalPhotos} 张（上限 {MAX_PHOTOS_TOTAL} 张）
                    </Text>
                    <Button
                        type="primary"
                        icon={<CameraOutlined />}
                        size="small"
                        onClick={() => setShowNewForm(true)}
                        disabled={showNewForm}
                    >
                        新增记录
                    </Button>
                </div>

                {/* 新建事件表单 */}
                {showNewForm && (
                    <NewEventForm
                        onAdd={handleAddEvent}
                        onCancel={() => setShowNewForm(false)}
                        totalPhotos={totalPhotos}
                    />
                )}

                {/* 事件时间线 */}
                {events.length > 0 ? (
                    <Timeline
                        items={events.map((event) => ({
                            key: event.event_id,
                            color: (event.items?.length || 0) > 0 ? "blue" : "gray",
                            children: (
                                <EventCard
                                    event={event}
                                    signedUrls={signedUrls}
                                    totalPhotos={totalPhotos}
                                    onRemoveEvent={handleRemoveEvent}
                                    onUpdateEvent={handleUpdateEvent}
                                    onUploadPhoto={handleUploadPhoto}
                                    onRemovePhoto={handleRemovePhoto}
                                    onPreview={handlePreview}
                                    onCaptionChange={handleCaptionChange}
                                    uploading={uploading}
                                />
                            ),
                        }))}
                    />
                ) : (
                    !showNewForm && (
                        <Empty description="暂无照片记录" style={{ margin: "32px 0" }}>
                            <Button
                                type="primary"
                                icon={<CameraOutlined />}
                                onClick={() => setShowNewForm(true)}
                            >
                                新增第一条记录
                            </Button>
                        </Empty>
                    )
                )}
            </Spin>

            <Image
                style={{ display: "none" }}
                preview={{
                    visible: previewVisible,
                    src: previewUrl,
                    onVisibleChange: (vis) => setPreviewVisible(vis),
                }}
            />
        </Modal>
    );
};

export default UldPhotoModal;
